// Phase 24D — SE Day-End Summary mobile screen.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ListChecks, Store, PackageCheck, TrendingUp, IndianRupee, Cloud, ShieldCheck, Eye } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function SeDailySummary() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/summary/today");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const p = data?.payload || data?.preview || {};
  const isDelivered = !!data?.exists;

  async function markViewed() {
    if (!isDelivered || data?.viewed_at) return;
    setBusy(true);
    try {
      await api.post(`/summary/${data.id}/viewed`);
      toast.success("Marked as viewed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="p-4 space-y-3" data-testid="se-daily-summary-page">
      <div className="text-center">
        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-1" />
        <h1 className="text-xl font-bold">Day-End Summary</h1>
        <p className="text-xs text-neutral-500">{data?.date || new Date().toISOString().slice(0, 10)}</p>
        {isDelivered && <p className="text-[11px] mt-1 text-emerald-600">
          Delivered {data?.delivered_at?.slice(11, 16)} · via {data?.source === "auto_8pm" ? "8 PM auto" : "EOD checkout"}
        </p>}
        {!isDelivered && <p className="text-[11px] mt-1 text-neutral-400">Preview (not yet delivered)</p>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Tile icon={<ListChecks className="w-4 h-4 text-blue-600" />} label="Visits Planned"
              value={p.visits_planned ?? 0} testid="tile-planned" />
        <Tile icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />} label="Visits Done"
              value={p.visits_done ?? 0} testid="tile-done" />
        <Tile icon={<TrendingUp className="w-4 h-4 text-purple-600" />} label="Productive %"
              value={`${(p.productive_pct ?? 0).toFixed(1)}%`} testid="tile-productive" />
        <Tile icon={<Store className="w-4 h-4 text-orange-600" />} label="New Calls"
              value={p.new_calls ?? 0} testid="tile-new-calls" />
        <Tile icon={<PackageCheck className="w-4 h-4 text-indigo-600" />} label="Orders"
              value={p.orders_count ?? 0} sub={`₹${(p.orders_value ?? 0).toLocaleString("en-IN")}`}
              testid="tile-orders" />
        <Tile icon={<IndianRupee className="w-4 h-4 text-teal-600" />} label="TA/DA"
              value={(p.tada_claim_status || "none").toUpperCase()} testid="tile-tada" />
        <Tile icon={<Cloud className="w-4 h-4 text-slate-600" />} label="Pending Syncs"
              value={p.pending_syncs ?? 0} testid="tile-pending" fullwidth />
      </div>

      {isDelivered && !data?.viewed_at && (
        <Button onClick={markViewed} disabled={busy}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="mark-viewed-btn">
          <Eye className="w-4 h-4 mr-1" /> Mark as Viewed
        </Button>
      )}
      {isDelivered && data?.viewed_at && (
        <div className="text-center text-xs text-emerald-600 flex items-center justify-center gap-1">
          <ShieldCheck className="w-3 h-3" /> Viewed {data.viewed_at?.slice(11, 16)}
        </div>
      )}
    </div>
  );
}

function Tile({ icon, label, value, sub, testid, fullwidth }) {
  return (
    <Card className={`border-neutral-200 rounded-2xl ${fullwidth ? "col-span-2" : ""}`} data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500">
          {icon}{label}
        </div>
        <div className="text-2xl font-bold mt-1">{value}</div>
        {sub && <div className="text-xs text-neutral-500">{sub}</div>}
      </CardContent>
    </Card>
  );
}
